<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Core
import { computed, nextTick, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"
import * as yup from "yup"

// Form validation
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"

// PrimeVue components
import {
    InputNumber as BaseInputNumber,
    ToggleSwitch as BaseToggleSwitch,
} from "primevue"

// Internal types and constants
import {
    CAPACITY_METHOD_ENERGIE_AVEC_COEFF,
    CAPACITY_METHOD_ENERGIE_SANS_COEFF,
    CAPACITY_METHOD_PUISSANCE,
    DEFAULT_COEFF_CAPACITE_VALUE,
    DEFAULT_PRICER_CAPACITY_VALUE,
    OptionTarifaire,
    PLAGE_ORDER,
    PLAGE_ORDER_PRICE,
} from "@modules/courtage/constants"
import { CourtageFormFieldConfig, PeriodItem } from "@types"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { storeToRefs } from "pinia"
import { formatNumber } from "@core/helpers"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Période {index}",
            supplier: "Nom du fournisseur",
            offer_name: "Nom de l'offre",
            offer_type: "Type de l'offre",
            duplicate: "Dupliquer la période",
            period: "Définisser l'année à laquelle s'applique cette offre.",
            commun_fields: {
                abonnement: {
                    label: "Prix de l'abonnement",
                    suffix: "€",
                    hint: "Prix mensuel HTT",
                },
                mode_calcul_capacite: {
                    title: "Calcul de la capacité obligatoire",
                    label_puissance: "Par puissance",
                    label_energie_avec_coeff:
                        "Par énergie avec coefficient de capacité",
                    label_energie_sans_coeff:
                        "Par énergie sans coefficient de capacité",
                },
                capacite_obligatoire: {
                    label: "Prix de la capacité obligatoire",
                    suffix: "€/kW/an",
                    suffix_mwh: "€/MWh",
                    hint: "Prix annuel HTT",
                    hint_mwh: "Prix en €/MWh par an (saisi tel quel)",
                },
                coefficient_capacite: {
                    label: "Coefficient de capacité",
                    suffix: "kW/MWH",
                    base: "Coeff. capacité (Base)",
                    hp: "Coeff. capacité heures pleines",
                    hc: "Coeff. capacité heures creuses",
                    hpb: "Coeff. capacité heures pleines saison basse",
                    hcb: "Coeff. capacité heures creuses saison basse",
                    hph: "Coeff. capacité heures pleines saison haute",
                    hch: "Coeff. capacité heures creuses saison haute",
                    pte: "Coeff. capacité heures de pointe",
                    hint: "Valeur comprise entre 0 et 1",
                },
                cee: {
                    label: "Prix du certificat d’économie d’énergie",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
                turpe: {
                    label: "Acheminement inclus",
                },
                puissance_oc: {
                    label: "Puissance retenue",
                    suffix: ({
                        named,
                    }: {
                        named: (key: string) => boolean
                    }) => {
                        const isC5 = named("c5")

                        const hasPTE = named("pte")

                        if (isC5) {
                            return "kVA"
                        }
                        return hasPTE ? "kVA" : "kW"
                    },
                    message: ({
                        named,
                    }: {
                        named: (key: string) => boolean
                    }) => {
                        const isC5 = named("c5")

                        if (isC5) {
                            return "La Puissance Obligatoire de Capacité correspond à la puissance souscrite (fixe, vous ne pouvez pas le modifier)."
                        }

                        return "La Puissance Obligatoire de Capacité correspond à <strong>la valeur la plus élevée</strong> du dénivelé de puissance (fixe, vous ne pouvez pas le modifier)."
                    },
                },
            },
            base_fields: {
                prix_base: {
                    label: "Prix de l'énergie en base",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
            },
            hphc_fields: {
                prix_hp: {
                    label: "Prix en heures pleines",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
                prix_hc: {
                    label: "Prix en heures creuses",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
            },
            hs_fields: {
                prix_hpb: {
                    label: "Prix en heures pleines saison basse",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
                prix_hcb: {
                    label: "Prix en heures creuses saison basse",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
                prix_hch: {
                    label: "Prix en heures creuses saison haute",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
                prix_hph: {
                    label: "Prix en heures pleines saison haute",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
                prix_pte: {
                    label: "Prix en heures de pointes",
                    suffix: "€/MWh",
                    hint: "Prix HTT",
                },
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Payload émis lors de la mise à jour d'une période
 */
interface PeriodUpdatePayload {
    /** Indique si la période est valide */
    valid: boolean
    /** Données de la période */
    period: PeriodItem
}

// ================================
// PROPS & EMITS
// ================================
/**
 * Propriétés du composant
 */
interface Props {
    /** Indique si c'est la période actuelle */
    actual: boolean
    /** Index de la période dans la liste */
    index: number
    /** Données de la période (peut être null pour une nouvelle période) */
    period: PeriodItem | null
    /** Option tarifaire sélectionnée */
    offer?: OptionTarifaire
    /** Modèle de consommation par poste pour l'offre */
    energieparposteElec?: {
        base: number
        hp: number
        hc: number
        hph: number
        hch: number
        hpb: number
        hcb: number
        pte: number
    }
}

const props = withDefaults(defineProps<Props>(), {
    actual: false,
    index: 0,
    period: null,
    offer: OptionTarifaire.Base,
    energieparposteElec: undefined,
})

/**
 * Événements émis par le composant
 */
const emit = defineEmits<{
    /** Événement de duplication de la période */
    (e: "click:duplicate"): void
    /** Événement de suppression de la période */
    (e: "click:delete"): void
    /** Événement de mise à jour des données de la période */
    (e: "update:period", value: PeriodUpdatePayload): void
}>()

// ================================
// REACTIVE STATE
// ================================
/**
 * Store du comparateur pour gérer les fournisseurs et sources
 */
const comparatorStore = useComparatorStore()

/**
 * Données réactives extraites du store
 */
const {
    isFetchingConsoParPlage,
    getPuissanceOc,
    getDenivelePuissance,
    getSourceById,
} = storeToRefs(comparatorStore)

/** État de l'accordéon (panneau actif) */
const active = ref<string>("0")

// ================================
// COMPUTED PROPERTIES
// ================================

const getPuissanceSouscrite = computed(() => {
    return getSourceById.value?.informations?.pdl?.structure_tarifaire
        ?.puissance_souscrite_soutirage?.puissance
})

/**
 * Formate la consommation annuelle selon les périodes HP/HC
 * @returns HTML formaté de la consommation annuelle
 */
const getConsommationAnnuelleFormatted = computed(() => {
    if (Object.keys(props.energieparposteElec ?? {}).length > 0) {
        // Sort entries according to PLAGE_ORDER
        const sortedEntries = Object.entries(
            props.energieparposteElec ?? {}
        ).sort(([plageA], [plageB]) => {
            const indexA = PLAGE_ORDER.indexOf(plageA as any)
            const indexB = PLAGE_ORDER.indexOf(plageB as any)

            // Handle cases where plage might not be in PLAGE_ORDER
            if (indexA === -1) return 1
            if (indexB === -1) return -1

            return indexA - indexB
        })

        const reducedEntries = sortedEntries.reduce(
            (acc, [plage, consommation]) => {
                if (acc?.[plage]) {
                    return acc
                }
                acc[plage] = consommation
                return acc
            },
            {} as Record<string, number>
        )

        // Renome "HPB" et "HCB" en "HPE" et "HCE"
        if (reducedEntries["HPB"] !== undefined) {
            reducedEntries["HPE"] = reducedEntries["HPB"]
            delete reducedEntries["HPB"]
        }
        if (reducedEntries["HCB"] !== undefined) {
            reducedEntries["HCE"] = reducedEntries["HCB"]
            delete reducedEntries["HCB"]
        }

        return reducedEntries
    }

    return "-"
})

/**
 * Consommation annuelle convertie en MWh pour l'affichage MeterPdlDeniveleViewer
 * @returns Dénivelé en MWh/an ou undefined si pas de données
 */
const getConsommationAnnuelleEnMWh = computed(() => {
    const formatted = getConsommationAnnuelleFormatted.value
    if (formatted === "-" || typeof formatted !== "object") {
        return undefined
    }
    return Object.fromEntries(
        Object.entries(formatted).map(([plage, kwh]) => [plage, kwh / 1000])
    ) as Record<string, number>
})

/**
 * Génère les options d'années pour le sélecteur de période
 * @returns Liste des 20 prochaines années
 */
const getYearOptions = computed((): string[] => {
    return Array.from({ length: 20 }, (_, i) =>
        moment()
            .add(10 - i, "years")
            .format("YYYY")
    )
})

/**
 * Retourne la configuration des champs en fonction de l'option tarifaire
 * @returns Configuration des champs dynamiques selon l'option tarifaire
 */
const getFieldsByOptionTarifaire = computed((): CourtageFormFieldConfig[] => {
    // Champs communs à toutes les options tarifaires
    const communFields: CourtageFormFieldConfig[] = [
        {
            label: t("commun_fields.abonnement.label"),
            inputId: "abonnement",
            component: BaseInputNumber,
            position: 0,
            hint: t("commun_fields.abonnement.hint"),
            options: {
                group_suffix: t("commun_fields.abonnement.suffix"),
                minFractionDigits: 2,
                maxFractionDigits: 7,
                min: 0,
                locale: "fr-FR",
            },
            model: abonnement,
        },
        {
            label: t("commun_fields.turpe.label"),
            inputId: "turpe",
            component: BaseToggleSwitch,
            orientation: "horizontal",
            position: 2,
            model: turpe,
        },
    ]

    // Champs spécifiques selon l'option tarifaire
    const fieldsByOptionTarifaire: CourtageFormFieldConfig[] = []

    switch (props.offer) {
        case OptionTarifaire.Base:
            fieldsByOptionTarifaire.push({
                label: t("base_fields.prix_base.label"),
                inputId: "prix_base",
                component: BaseInputNumber,
                position: 1,
                hint: t("base_fields.prix_base.hint"),
                options: {
                    group_suffix: t("base_fields.prix_base.suffix"),
                    minFractionDigits: 2,
                    maxFractionDigits: 7,
                    min: 0,
                    locale: "fr-FR",
                },
                model: prix_base,
            })
            break
        case OptionTarifaire.HPHC:
            fieldsByOptionTarifaire.push(
                ...[
                    {
                        label: t("hphc_fields.prix_hp.label"),
                        inputId: "prix_hp",
                        component: BaseInputNumber,
                        position: 1,
                        hint: t("hphc_fields.prix_hp.hint"),
                        options: {
                            group_suffix: t("hphc_fields.prix_hp.suffix"),
                            minFractionDigits: 2,
                            maxFractionDigits: 7,
                            min: 0,
                            locale: "fr-FR",
                        },
                        model: prix_hp,
                    },
                    {
                        label: t("hphc_fields.prix_hc.label"),
                        inputId: "prix_hc",
                        component: BaseInputNumber,
                        position: 1,
                        hint: t("hphc_fields.prix_hc.hint"),
                        options: {
                            group_suffix: t("hphc_fields.prix_hc.suffix"),
                            minFractionDigits: 2,
                            maxFractionDigits: 7,
                            min: 0,
                            locale: "fr-FR",
                        },
                        model: prix_hc,
                    },
                ]
            )
            break
        case OptionTarifaire.HS:
        case OptionTarifaire.HS_PTE_Fixe:
            fieldsByOptionTarifaire.push(
                ...[
                    {
                        label: t("hs_fields.prix_hph.label"),
                        inputId: "prix_hph",
                        component: BaseInputNumber,
                        position: 1,
                        hint: t("hs_fields.prix_hph.hint"),
                        options: {
                            group_suffix: t("hs_fields.prix_hph.suffix"),
                            minFractionDigits: 2,
                            maxFractionDigits: 7,
                            min: 0,
                            locale: "fr-FR",
                        },
                        model: prix_hph,
                    },
                    {
                        label: t("hs_fields.prix_hch.label"),
                        inputId: "prix_hch",
                        component: BaseInputNumber,
                        position: 2,
                        hint: t("hs_fields.prix_hch.hint"),
                        options: {
                            group_suffix: t("hs_fields.prix_hch.suffix"),
                            minFractionDigits: 2,
                            maxFractionDigits: 7,
                            min: 0,
                            locale: "fr-FR",
                        },
                        model: prix_hch,
                    },
                    {
                        label: t("hs_fields.prix_hpb.label"),
                        inputId: "prix_hpb",
                        component: BaseInputNumber,
                        position: 1,
                        hint: t("hs_fields.prix_hpb.hint"),
                        options: {
                            group_suffix: t("hs_fields.prix_hpb.suffix"),
                            minFractionDigits: 2,
                            maxFractionDigits: 7,
                            min: 0,
                            locale: "fr-FR",
                        },
                        model: prix_hpb,
                    },
                    {
                        label: t("hs_fields.prix_hcb.label"),
                        inputId: "prix_hcb",
                        component: BaseInputNumber,
                        position: 1,
                        hint: t("hs_fields.prix_hcb.hint"),
                        options: {
                            group_suffix: t("hs_fields.prix_hcb.suffix"),
                            minFractionDigits: 2,
                            maxFractionDigits: 7,
                            min: 0,
                            locale: "fr-FR",
                        },
                        model: prix_hcb,
                    },
                ]
            )

            // Ajout du prix de l'énergie en heures pleines saison basse si l'option tarifaire est HS_PTE_Fixe
            if (props.offer === OptionTarifaire.HS_PTE_Fixe) {
                fieldsByOptionTarifaire.push({
                    label: t("hs_fields.prix_pte.label"),
                    inputId: "prix_pte",
                    component: BaseInputNumber,
                    position: 1,
                    hint: t("hs_fields.prix_pte.hint"),
                    options: {
                        group_suffix: t("hs_fields.prix_pte.suffix"),
                        minFractionDigits: 2,
                        maxFractionDigits: 7,
                        min: 0,
                        locale: "fr-FR",
                    },
                    model: prix_pte,
                })
            }
            break
        default:
            break
    }

    const fields = [...communFields, ...fieldsByOptionTarifaire]

    // Trier les champs par position
    fields.sort((a, b) => {
        // return positionA - positionB
        const indexA = PLAGE_ORDER_PRICE.indexOf(a.inputId)
        const indexB = PLAGE_ORDER_PRICE.indexOf(b.inputId)

        // If not found in order array, put at the end
        const positionA = indexA !== -1 ? indexA : Infinity
        const positionB = indexB !== -1 ? indexB : Infinity

        return positionA - positionB
    })

    return fields
})

/**
 * Retourne les champs communs à toutes les options tarifaires
 * @returns Configuration des champs communs
 */
const getCommunFields = computed((): CourtageFormFieldConfig[] => {
    const fields: CourtageFormFieldConfig[] = []

    fields.push({
        label: t("commun_fields.cee.label"),
        inputId: "cee",
        component: BaseInputNumber,
        hint: t("hs_fields.prix_hph.hint"),
        options: {
            group_suffix: t("commun_fields.cee.suffix"),
            minFractionDigits: 2,
            maxFractionDigits: 7,
            min: 0,
        },
        model: cee,
    })

    return fields
})

/**
 * Options du sélecteur de méthode de calcul de la capacité obligatoire
 */
const getCapacityMethodOptions = computed(() => [
    {
        label: t("commun_fields.mode_calcul_capacite.label_puissance"),
        value: CAPACITY_METHOD_PUISSANCE,
    },
    {
        label: t("commun_fields.mode_calcul_capacite.label_energie_avec_coeff"),
        value: CAPACITY_METHOD_ENERGIE_AVEC_COEFF,
    },
    {
        label: t("commun_fields.mode_calcul_capacite.label_energie_sans_coeff"),
        value: CAPACITY_METHOD_ENERGIE_SANS_COEFF,
    },
])

/**
 * Retourne la configuration des champs pour la capacité selon le mode sélectionné
 */
const getCapacityFields = computed((): CourtageFormFieldConfig[] => {
    const fields: CourtageFormFieldConfig[] = []

    // Si on utilise le coefficient de capacité (méthode 2 uniquement)
    if (capacity_method.value === CAPACITY_METHOD_ENERGIE_AVEC_COEFF) {
        // En fonction du type d'offre choisi on affiche le champ correspondant

        const options = {
            group_suffix: t("commun_fields.coefficient_capacite.suffix"),
            minFractionDigits: 2,
            maxFractionDigits: 7,
            min: 0,
            locale: "fr-FR",
            max: 1,
        }
        switch (props.offer) {
            case OptionTarifaire.Base:
                fields.push({
                    label: t("commun_fields.coefficient_capacite.base"),
                    inputId: "coefficient_capacite_base",
                    component: BaseInputNumber,
                    hint: t("commun_fields.coefficient_capacite.hint"),
                    options,
                    model: coefficient_capacite_base,
                })
                break
            case OptionTarifaire.HPHC:
                ;["hp", "hc"].forEach((key) => {
                    fields.push({
                        label: t(`commun_fields.coefficient_capacite.${key}`),
                        inputId: `coefficient_capacite_${key}`,
                        component: BaseInputNumber,
                        hint: t("commun_fields.coefficient_capacite.hint"),
                        options,
                        model:
                            key === "hp"
                                ? coefficient_capacite_hp
                                : coefficient_capacite_hc,
                    })
                })
                break

            case OptionTarifaire.HS:
            case OptionTarifaire.HS_PTE_Fixe:
                ;["hpb", "hcb", "hph", "hch"].forEach((key) => {
                    fields.push({
                        label: t(`commun_fields.coefficient_capacite.${key}`),
                        inputId: `coefficient_capacite_${key}`,
                        component: BaseInputNumber,
                        hint: t("commun_fields.coefficient_capacite.hint"),
                        options,
                        model:
                            key === "hpb"
                                ? coefficient_capacite_hpb
                                : key === "hcb"
                                  ? coefficient_capacite_hcb
                                  : key === "hph"
                                    ? coefficient_capacite_hph
                                    : coefficient_capacite_hch,
                    })
                })
                // Dans le cas de l'option HS_PTE_Fixe, on ajoute le champ pour la Pointe
                if (props.offer === OptionTarifaire.HS_PTE_Fixe) {
                    fields.push({
                        label: t(`commun_fields.coefficient_capacite.pte`),
                        inputId: "coefficient_capacite_ptes",
                        component: BaseInputNumber,
                        hint: t("commun_fields.coefficient_capacite.hint"),
                        options,
                        model: coefficient_capacite_pte,
                    })
                }
                break
        }
    }

    const isEnergieSansCoeff =
        capacity_method.value === CAPACITY_METHOD_ENERGIE_SANS_COEFF
    fields.push({
        label: t("commun_fields.capacite_obligatoire.label"),
        inputId: "capacite_obligatoire",
        component: BaseInputNumber,
        hint: isEnergieSansCoeff
            ? t("commun_fields.capacite_obligatoire.hint_mwh")
            : t("commun_fields.capacite_obligatoire.hint"),
        options: {
            group_suffix: isEnergieSansCoeff
                ? t("commun_fields.capacite_obligatoire.suffix_mwh")
                : t("commun_fields.capacite_obligatoire.suffix"),
            minFractionDigits: 2,
            maxFractionDigits: 7,
            min: 0,
            locale: "fr-FR",
        },
        model: capacite_obligatoire,
    })

    return fields
})

/**
 * Schéma de validation dynamique selon l'option tarifaire
 * @returns {yup.ObjectSchema<any>} Schéma de validation Yup
 */
const typedSchema = computed(() =>
    toTypedSchema(getSchemaByOptionTarifaire(props.offer))
)

// ================================
// FORM CONFIGURATION
// ================================
/**
 * Configuration du formulaire avec validation
 */
const { errors, defineField, setFieldValue, values, meta, validate } =
    useForm<PeriodItem>({
        validationSchema: typedSchema,
        initialValues: {
            idperiode: props.period?.idperiode,
            cee: props.period?.cee || 0,
            // Prix de la capacité obligatoire
            capacite_obligatoire:
                props.period?.capacite_obligatoire ||
                DEFAULT_PRICER_CAPACITY_VALUE,
            capacity_method:
                props.period?.capacity_method || CAPACITY_METHOD_PUISSANCE,
            abonnement: props.period?.abonnement || 0,
            turpe: props.period?.turpe || false,
            // Prix de consommation
            prix_base: props.period?.prix_base || 0,
            prix_hp: props.period?.prix_hp || 0,
            prix_hc: props.period?.prix_hc || 0,
            prix_hph: props.period?.prix_hph || 0,
            prix_hch: props.period?.prix_hch || 0,
            prix_hpb: props.period?.prix_hpb || 0,
            prix_hcb: props.period?.prix_hcb || 0,
            prix_pte: props.period?.prix_pte || 0,
            // Coefficient de capacité : WITHOUT_COEFF pour "energie_sans_coeff", sinon valeur période ou WITH_COEFF
            coefficient_capacite_base:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_base ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            coefficient_capacite_hp:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_hp ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            coefficient_capacite_hc:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_hc ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            coefficient_capacite_hph:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_hph ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            coefficient_capacite_hch:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_hch ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            coefficient_capacite_hpb:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_hpb ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            coefficient_capacite_hcb:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_hcb ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            coefficient_capacite_pte:
                props.period?.capacity_method ===
                CAPACITY_METHOD_ENERGIE_SANS_COEFF
                    ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                    : (props.period?.coefficient_capacite_pte ??
                      DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
        },
    })

/**
 * Évite d'émettre au premier run du watch (valeurs formulaire pas encore
 * synchronisées avec props au montage, ce qui écraserait les coefficients).
 */
const isFirstWatchRun = ref(true)

// ================================
// LIFECYCLE
// ================================
/**
 * Au montage, force les champs du formulaire à partir de props.period pour
 * afficher les bonnes valeurs (ex. coefficients à 1) après passage en mode édition.
 */
onMounted(() => {
    nextTick(() => {
        const p = props.period
        if (!p) return
        const coeffFields = [
            "coefficient_capacite_base",
            "coefficient_capacite_hp",
            "coefficient_capacite_hc",
            "coefficient_capacite_hph",
            "coefficient_capacite_hch",
            "coefficient_capacite_hpb",
            "coefficient_capacite_hcb",
            "coefficient_capacite_pte",
        ] as const
        for (const field of coeffFields) {
            const v = p[field]
            if (v !== undefined && v !== null) {
                setFieldValue(field, v)
            }
        }
    })
})

// ================================
// WATCHERS
// ================================
/**
 * Surveille les changements de valeurs et de validation du formulaire
 * Émet automatiquement les mises à jour vers le composant parent
 */
watch(
    [values, meta],
    ([newValues, newMeta]) => {
        if (isFirstWatchRun.value) {
            isFirstWatchRun.value = false
            return
        }
        const periodFromProps = props.period
        emit("update:period", {
            valid: newMeta.valid,
            period: {
                idperiode: newValues.idperiode ?? periodFromProps?.idperiode,
                name: periodFromProps?.name ?? `Période ${props.index}`,
                cee: newValues.cee ?? periodFromProps?.cee ?? 0,
                // Prix de la capacité obligatoire
                capacite_obligatoire:
                    newValues.capacite_obligatoire ??
                    periodFromProps?.capacite_obligatoire,
                // Indique si on utilise le coefficient de capacité
                capacity_method:
                    newValues.capacity_method ??
                    periodFromProps?.capacity_method ??
                    CAPACITY_METHOD_PUISSANCE,
                abonnement:
                    newValues.abonnement ?? periodFromProps?.abonnement ?? 0,
                turpe: newValues.turpe ?? periodFromProps?.turpe ?? false,
                // Prix de consommation
                prix_base:
                    newValues.prix_base ?? periodFromProps?.prix_base ?? 0,
                prix_hp: newValues.prix_hp ?? periodFromProps?.prix_hp ?? 0,
                prix_hc: newValues.prix_hc ?? periodFromProps?.prix_hc ?? 0,
                prix_hph: newValues.prix_hph ?? periodFromProps?.prix_hph ?? 0,
                prix_hch: newValues.prix_hch ?? periodFromProps?.prix_hch ?? 0,
                prix_hpb: newValues.prix_hpb ?? periodFromProps?.prix_hpb ?? 0,
                prix_hcb: newValues.prix_hcb ?? periodFromProps?.prix_hcb ?? 0,
                prix_pte: newValues.prix_pte ?? periodFromProps?.prix_pte ?? 0,
                // Coefficient de capacité : pour "energie_sans_coeff" on envoie WITHOUT_COEFF partout (masqué) ; sinon préserver les valeurs existantes
                coefficient_capacite_base:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_base ??
                          periodFromProps?.coefficient_capacite_base ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
                coefficient_capacite_hp:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_hp ??
                          periodFromProps?.coefficient_capacite_hp ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
                coefficient_capacite_hc:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_hc ??
                          periodFromProps?.coefficient_capacite_hc ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
                coefficient_capacite_hph:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_hph ??
                          periodFromProps?.coefficient_capacite_hph ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
                coefficient_capacite_hch:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_hch ??
                          periodFromProps?.coefficient_capacite_hch ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
                coefficient_capacite_hpb:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_hpb ??
                          periodFromProps?.coefficient_capacite_hpb ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
                coefficient_capacite_hcb:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_hcb ??
                          periodFromProps?.coefficient_capacite_hcb ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
                coefficient_capacite_pte:
                    newValues.capacity_method ===
                    CAPACITY_METHOD_ENERGIE_SANS_COEFF
                        ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                        : (newValues.coefficient_capacite_pte ??
                          periodFromProps?.coefficient_capacite_pte ??
                          DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF),
            },
        })
    },
    { deep: true }
)
// ================================
// METHODS
// ================================
/**
 * Génère le schéma de validation selon l'option tarifaire sélectionnée
 * @param offer - L'option tarifaire
 * @returns Schéma de validation Yup
 */
function getSchemaByOptionTarifaire(
    offer: OptionTarifaire | undefined
): yup.ObjectSchema<any> {
    // Helper pour créer un schéma de coefficient de capacité
    const coefficientField = () =>
        yup.number().when("capacity_method", {
            is: CAPACITY_METHOD_ENERGIE_AVEC_COEFF,
            then: (schema) =>
                schema.required().max(1, "Doit être inférieur ou égal à 11"),
            otherwise: (schema) => schema.notRequired(),
        })

    // Configuration des champs selon l'option tarifaire
    const priceFieldsConfig: Record<OptionTarifaire, string[]> = {
        [OptionTarifaire.Base]: ["prix_base"],
        [OptionTarifaire.HPHC]: ["prix_hp", "prix_hc"],
        [OptionTarifaire.HS]: ["prix_hph", "prix_hch", "prix_hpb", "prix_hcb"],
        [OptionTarifaire.HS_PTE_Fixe]: [
            "prix_hph",
            "prix_hch",
            "prix_hpb",
            "prix_hcb",
            "prix_pte",
        ],
    }

    const coefficientFieldsConfig: Record<OptionTarifaire, string[]> = {
        [OptionTarifaire.Base]: ["base"],
        [OptionTarifaire.HPHC]: ["hp", "hc"],
        [OptionTarifaire.HS]: ["hph", "hch", "hpb", "hcb"],
        [OptionTarifaire.HS_PTE_Fixe]: ["hph", "hch", "hpb", "hcb", "pte"],
    }

    // Génération dynamique des schémas de prix
    const priceFields = (priceFieldsConfig[offer!] || []).reduce(
        (acc, field) => {
            acc[field] = yup.number().required()
            return acc
        },
        {} as Record<string, yup.Schema>
    )

    // Génération dynamique des schémas de coefficients
    const coefficientFields = (coefficientFieldsConfig[offer!] || []).reduce(
        (acc, field) => {
            acc[`coefficient_capacite_${field}`] = coefficientField()
            return acc
        },
        {} as Record<string, yup.Schema>
    )

    // Schéma final
    return yup.object({
        ...priceFields,
        ...coefficientFields,
        cee: yup.number().required(),
        capacite_obligatoire: yup.number().required(),
        coefficient_capacite: yup.object().when("capacity_method", {
            is: CAPACITY_METHOD_ENERGIE_AVEC_COEFF,
            then: (schema) => schema.required().shape(coefficientFields),
            otherwise: (schema) => schema.notRequired(),
        }),
        capacity_method: yup.string().required(),
        abonnement: yup.number().required(),
        turpe: yup.boolean().required(),
        idperiode: yup.string().required(),
    })
}

// ================================
// FORM FIELD BINDINGS
// ================================
/** Champ pour l'identifiant de la période */
const [idperiode] = defineField("idperiode")
/** Champ pour le prix de la CEE */
const [cee] = defineField("cee")
/** Champ pour le prix de la capacité obligatoire */
const [capacite_obligatoire] = defineField("capacite_obligatoire")

/** Champ pour indiquer la méthode de calcul de la capacité */
const [capacity_method] = defineField("capacity_method")
/** Champ pour le prix de l'abonnement */
const [abonnement] = defineField("abonnement")
/** Champ pour l'inclusion de l'acheminement TURPE */
const [turpe] = defineField("turpe")
/** Champ pour le prix de l'énergie en base */
const [prix_base] = defineField("prix_base")
/** Champ pour le prix en heures pleines */
const [prix_hp] = defineField("prix_hp")
/** Champ pour le prix en heures creuses */
const [prix_hc] = defineField("prix_hc")
/** Champ pour le prix en heures pleines saison haute */
const [prix_hph] = defineField("prix_hph")
/** Champ pour le prix en heures creuses saison haute */
const [prix_hch] = defineField("prix_hch")
/** Champ pour le prix en heures pleines saison basse */
const [prix_hpb] = defineField("prix_hpb")
/** Champ pour le prix en heures creuses saison basse */
const [prix_hcb] = defineField("prix_hcb")
/** Champ pour le prix en heures pleines saison basse (option PTE fixe) */
const [prix_pte] = defineField("prix_pte")

/** Champ pour le coefficient de capacité */
const [coefficient_capacite_base] = defineField("coefficient_capacite_base")
/** Champ pour le coefficient de capacité en heures pleines */
const [coefficient_capacite_hp] = defineField("coefficient_capacite_hp")
/** Champ pour le coefficient de capacité en heures creuses */
const [coefficient_capacite_hc] = defineField("coefficient_capacite_hc")
/** Champ pour le coefficient de capacité en heures pleines saison haute */
const [coefficient_capacite_hph] = defineField("coefficient_capacite_hph")
/** Champ pour le coefficient de capacité en heures creuses saison haute */
const [coefficient_capacite_hch] = defineField("coefficient_capacite_hch")
/** Champ pour le coefficient de capacité en heures pleines saison basse */
const [coefficient_capacite_hpb] = defineField("coefficient_capacite_hpb")
/** Champ pour le coefficient de capacité en heures creuses saison basse */
const [coefficient_capacite_hcb] = defineField("coefficient_capacite_hcb")
/** Champ pour le coefficient de capacité en heures de pointe */
const [coefficient_capacite_pte] = defineField("coefficient_capacite_pte")

const isValid = computed(() => {
    return meta.value.valid
})

/**
 * Surveille les changements de l'option tarifaire sélectionnée
 * Réinitialise le coefficient de capacité en conséquence (ou WITHOUT_COEFF si méthode "energie_sans_coeff")
 */
watch(
    () => props.offer,
    () => {
        const coeffValue =
            capacity_method.value === CAPACITY_METHOD_ENERGIE_SANS_COEFF
                ? DEFAULT_COEFF_CAPACITE_VALUE.WITHOUT_COEFF
                : DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF
        coefficient_capacite_base.value = coeffValue
        coefficient_capacite_hp.value = coeffValue
        coefficient_capacite_hc.value = coeffValue
        coefficient_capacite_hph.value = coeffValue
        coefficient_capacite_hch.value = coeffValue
        coefficient_capacite_hpb.value = coeffValue
        coefficient_capacite_hcb.value = coeffValue
        coefficient_capacite_pte.value = coeffValue
    },
    {
        immediate: true,
    }
)

defineExpose({
    isValid,
    validate,
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <BaseAccordion
        v-model:value="active"
        class="comparator-offer-item-period-item"
    >
        <BaseAccordionPanel value="0">
            <!-- ================================ -->
            <!-- HEADER/EN-TÊTE -->
            <!-- ================================ -->
            <BaseAccordionHeader
                class="flex-row-reverse! justify-end! gap-2! bg-transparent!"
            >
                <!-- Titre de la période avec actions -->
                <h6
                    class="md:flex md:items-center gap-2 pl-2! text-xs sm:text-base grid grid-cols-2"
                >
                    <span>{{ t("title", { index }) }}</span>
                    <span class="text-cyan-500 hidden md:block">•</span>

                    <!-- Sélecteur de l'année -->
                    <BaseSelect
                        v-if="!actual"
                        class="border-none!"
                        v-model="idperiode"
                        :options="getYearOptions"
                        v-tooltip.top="t('period')"
                        :pt="{
                            dropdownIcon: 'text-cyan-500!',
                        }"
                        @click.prevent.stop
                    />

                    <!-- Affichage de l'année sélectionnée si la période est actuelle -->
                    <span v-else>{{ idperiode }}</span>

                    <span v-if="!actual" class="text-cyan-500 hidden md:block"
                        >•</span
                    >
                    <!-- Bouton de duplication -->
                    <BaseButton
                        v-if="!actual"
                        icon="pi pi-clone"
                        variant="link"
                        v-tooltip.top="t('duplicate')"
                        @click.prevent.stop="$emit('click:duplicate')"
                    />
                    <!-- Bouton de suppression -->
                    <BaseButton
                        v-if="!actual && index > 1"
                        icon="pi pi-trash"
                        variant="link"
                        @click.prevent.stop="$emit('click:delete')"
                    />
                </h6>
            </BaseAccordionHeader>

            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL -->
            <!-- ================================ -->
            <BaseAccordionContent :pt="{ content: 'rounded-b-md' }">
                <!-- ================================ -->
                <!-- FORMULAIRE DE LA PÉRIODE -->
                <!-- ================================ -->
                <form @submit.prevent.stop class="flex flex-col gap-2">
                    <!-- Champs dynamiques selon l'option tarifaire -->
                    <div
                        class="grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-y-8 gap-x-4 items-start"
                    >
                        <BaseInputWrapper
                            v-for="field in getFieldsByOptionTarifaire"
                            :key="`commun-${field.inputId}`"
                            :label="field.label"
                            :input-id="field.inputId"
                            unfloat
                            :orientation="field.orientation"
                            :suffix="field.options?.group_suffix"
                            :hint="field?.hint"
                            :error="
                                errors[field.inputId as keyof typeof errors]
                            "
                        >
                            <component
                                :id="field.inputId"
                                :is="field.component"
                                v-bind="field.options"
                                :input-id="field.inputId"
                                v-model="field.model"
                                :invalid="
                                    !!errors[
                                        field.inputId as keyof typeof errors
                                    ]
                                "
                            />
                        </BaseInputWrapper>
                    </div>
                    <BaseDivider />
                    <!-- Champs pour la puissance de capacité ou coefficient de capacités -->
                    <div
                        class="grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-y-8 gap-x-4 items-start"
                    >
                        <div
                            class="col-span-1 xl:col-span-4 md:col-span-2 flex flex-col gap-2"
                        >
                            <h5 class="font-bold text-sm">
                                {{
                                    t(
                                        "commun_fields.mode_calcul_capacite.title"
                                    )
                                }}
                            </h5>
                            <BaseSelect
                                id="capacity_method"
                                v-model="capacity_method"
                                option-label="label"
                                option-value="value"
                                :options="getCapacityMethodOptions"
                            />
                        </div>

                        <template
                            v-if="
                                capacity_method ===
                                CAPACITY_METHOD_ENERGIE_AVEC_COEFF
                            "
                        >
                            <MeterPdlDeniveleViewer
                                v-if="!isFetchingConsoParPlage"
                                class="col-span-1 xl:col-span-4 md:col-span-2"
                                :denivele="getConsommationAnnuelleEnMWh"
                                unit="MWh/an"
                            />
                            <BaseSkeleton v-else height="4rem" width="100%" />
                        </template>

                        <template
                            v-if="capacity_method === CAPACITY_METHOD_PUISSANCE"
                        >
                            <MeterPdlDeniveleViewer
                                v-if="
                                    !['C5', 'P4'].includes(
                                        getSourceById?.informations?.pdl
                                            ?.compteur?.segment ?? ''
                                    )
                                "
                                class="col-span-1 xl:col-span-4 md:col-span-2"
                                :denivele="getDenivelePuissance"
                            />

                            <BaseMessage
                                class="col-span-1 xl:col-span-4 md:col-span-2"
                            >
                                <p
                                    class="text-sm"
                                    v-html="
                                        t(
                                            'commun_fields.puissance_oc.message',
                                            {
                                                c5: !!getPuissanceSouscrite,
                                            }
                                        )
                                    "
                                ></p>
                            </BaseMessage>

                            <!-- Dans le cas du type "Puissance" on affiche la valeur calculé Puissance obligatoire capacité -->
                            <div class="flex items-end gap-2 col-span-1">
                                <BaseInputWrapper
                                    :label="
                                        t('commun_fields.puissance_oc.label')
                                    "
                                    :input-id="'puissance_oc'"
                                    unfloat
                                    :suffix="
                                        t('commun_fields.puissance_oc.suffix', {
                                            c5: ['C5', 'P4'].includes(
                                                getSourceById?.informations?.pdl
                                                    ?.compteur?.segment ?? ''
                                            ),
                                            pte:
                                                getDenivelePuissance?.PTE !=
                                                null,
                                        })
                                    "
                                >
                                    <BaseInputText
                                        id="puissance_oc"
                                        :value="
                                            formatNumber(getPuissanceOc, {
                                                defaultValue: '—',
                                            })
                                        "
                                        input-id="consommation_annuelle"
                                        disabled
                                    />
                                </BaseInputWrapper>
                            </div>
                        </template>

                        <BaseInputWrapper
                            v-for="field in getCapacityFields"
                            :key="field.inputId"
                            :label="field.label"
                            :input-id="field.inputId"
                            unfloat
                            :orientation="field.orientation"
                            :suffix="field.options?.group_suffix"
                            :hint="field?.hint"
                            :error="
                                errors[field.inputId as keyof typeof errors]
                            "
                            :class="
                                field.inputId === 'capacity_method'
                                    ? 'col-span-1 xl:col-span-4 md:col-span-2'
                                    : ''
                            "
                        >
                            <component
                                :id="field.inputId"
                                :is="field.component"
                                v-bind="field.options"
                                :input-id="field.inputId"
                                :model-value="field.model"
                                @update:model-value="
                                    field.onUpdateModel
                                        ? field.onUpdateModel($event)
                                        : null
                                "
                                :invalid="
                                    !!errors[
                                        field.inputId as keyof typeof errors
                                    ]
                                "
                            />
                        </BaseInputWrapper>
                    </div>
                    <BaseDivider />
                    <!-- Champs communs à toutes les options tarifaires -->
                    <div
                        class="grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-y-8 gap-x-4 items-start"
                    >
                        <BaseInputWrapper
                            v-for="field in getCommunFields"
                            :key="field.inputId"
                            :label="field.label"
                            :input-id="field.inputId"
                            unfloat
                            :orientation="field.orientation"
                            :suffix="field.options?.group_suffix"
                            :hint="field?.hint"
                            :error="
                                errors[field.inputId as keyof typeof errors]
                            "
                        >
                            <component
                                :id="field.inputId"
                                :is="field.component"
                                v-bind="field.options"
                                :input-id="field.inputId"
                                v-model="field.model"
                                :invalid="
                                    !!errors[
                                        field.inputId as keyof typeof errors
                                    ]
                                "
                            />
                        </BaseInputWrapper>
                    </div>
                </form>
            </BaseAccordionContent>
        </BaseAccordionPanel>
    </BaseAccordion>
</template>
